import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  Output,
  ViewChild,
  computed,
  input,
  model,
  signal,
} from '@angular/core';
import { timer } from 'rxjs';
import { IContentBuilderFieldColor } from '@core/models/content-builder-fields.model';
import { ColorFieldTypesValues } from '@config/values/color-field-types.values';
import { TreeTab } from '@core/models/tab-list.model';
import { CommonModule } from '@angular/common';
import { HorizontalScrollDirective } from '@shared/directives/horizontal-scroll.directive';
import { RoundedIconButtonComponent } from '../rounded-icon-button/rounded-icon-button.component';
import { ContentBuilderColorPipe } from '@core/pipes/content-builder-color.pipe';
import { CallComponentMethodPipe } from '@shared/pipes/call-component-method/call-component-method.pipe';
import { ScrollToIdDirective } from '@shared/directives/scroll-to-id.directive';

interface TreeTabUI extends TreeTab {
  isActive: boolean;
}

@Component({
  selector: 'app-tab-list-atom',
  templateUrl: './tab-list-atom.component.html',
  styleUrls: ['./tab-list-atom.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    HorizontalScrollDirective,
    RoundedIconButtonComponent,
    ContentBuilderColorPipe,
    CallComponentMethodPipe,
    ScrollToIdDirective,
  ],
  standalone: true,
})
export class TabListAtomComponent {
  readonly activeItemId = model<string | undefined>(undefined);
  readonly search = input<boolean>();
  readonly treeTabs = input.required<TreeTab[]>();
  readonly isNotched = input<boolean>();
  readonly displayBurgerNavigation = input(false);

  private readonly defaultCategoriesBackgroundColor: IContentBuilderFieldColor = {
    value: '#FFFFFF',
    type: ColorFieldTypesValues.Hex,
  };
  readonly categoriesBackgroundColor = input<IContentBuilderFieldColor>(
    this.defaultCategoriesBackgroundColor,
  );

  private readonly defaultCategoriesActiveColor: IContentBuilderFieldColor = {
    value: 'primary-600',
    type: ColorFieldTypesValues.Palette,
  };
  readonly categoriesActiveColor = input<IContentBuilderFieldColor>(
    this.defaultCategoriesActiveColor,
  );

  private readonly defaultCategoriesInactiveColor: IContentBuilderFieldColor = {
    value: 'neutral-600',
    type: ColorFieldTypesValues.Palette,
  };
  readonly categoriesInactiveColor = input<IContentBuilderFieldColor>(
    this.defaultCategoriesInactiveColor,
  );

  private readonly defaultBurgerBackgroundColor: IContentBuilderFieldColor = {
    value: 'primary-100',
    type: ColorFieldTypesValues.Palette,
  };
  readonly burgerBackgroundColor = input<IContentBuilderFieldColor>(
    this.defaultBurgerBackgroundColor,
  );

  private readonly defaultBurgerIconColor: IContentBuilderFieldColor = {
    value: 'primary-600',
    type: ColorFieldTypesValues.Palette,
  };
  readonly burgerIconColor = input<IContentBuilderFieldColor>(this.defaultBurgerIconColor);

  private readonly defaultSecondaryCategoriesColors = () => ({
    backgroundColor: {
      value: '#FFFFFF',
      type: ColorFieldTypesValues.Hex,
    },
    activeColor: this.categoriesActiveColor() ?? this.defaultCategoriesActiveColor,
    inactiveColor: this.categoriesActiveColor() ?? this.defaultCategoriesActiveColor,
  });

  readonly secondaryCategoriesColors = input<{
    backgroundColor: IContentBuilderFieldColor;
    activeColor: IContentBuilderFieldColor;
    inactiveColor: IContentBuilderFieldColor;
  }>(this.defaultSecondaryCategoriesColors());

  private readonly defaultThirdCategoriesColors = () => ({
    backgroundColor: {
      value: '#FFFFFF',
      type: ColorFieldTypesValues.Hex,
    },
    activeColor: this.secondaryCategoriesColors()?.activeColor ?? this.defaultCategoriesActiveColor,
    inactiveColor:
      this.secondaryCategoriesColors()?.activeColor ?? this.defaultCategoriesActiveColor,
  });

  readonly thirdCategoriesColors = input<{
    backgroundColor: IContentBuilderFieldColor;
    activeColor: IContentBuilderFieldColor;
    inactiveColor: IContentBuilderFieldColor;
  }>(this.defaultThirdCategoriesColors());

  protected readonly categoriesColorsByLevel = computed<
    Record<
      number,
      {
        backgroundColor: IContentBuilderFieldColor;
        activeColor: IContentBuilderFieldColor;
        inactiveColor: IContentBuilderFieldColor;
      }
    >
  >(() => {
    return {
      0: {
        backgroundColor: this.categoriesBackgroundColor(),
        activeColor: this.categoriesActiveColor(),
        inactiveColor: this.categoriesInactiveColor(),
      },
      1: {
        ...this.defaultSecondaryCategoriesColors(),
        ...this.secondaryCategoriesColors(),
      },
      2: {
        ...this.defaultThirdCategoriesColors(),
        ...this.thirdCategoriesColors(),
      },
    };
  });

  @Output() tabChange = new EventEmitter<{
    tabId: string;
  }>();
  @Output() searchEvent = new EventEmitter<Event>();
  @Output() isBurgerNavigationOpen = new EventEmitter<boolean>();

  @ViewChild('scrollSection') scrollSectionElement?: ElementRef;
  @ViewChild('btnMenu') btnMenu?: ElementRef;

  protected readonly activeMenu = signal(false);

  protected readonly treeTabsUI = computed(() => {
    return this.configureActiveTab({
      activeTabId: this.activeItemId(),
      treeTab: this.treeTabs(),
    });
  });

  private configureActiveTab(
    {
      activeTabId,
      treeTab,
    }: {
      activeTabId: string | undefined;
      treeTab: TreeTab[];
    },
    { isParentActive }: { isParentActive: boolean } = { isParentActive: false },
  ): TreeTabUI[] {
    const result = treeTab.map((tab, index) => {
      const isThisTabActive =
        activeTabId === tab.id || (!activeTabId && index === 0) || (isParentActive && index === 0);

      const children = tab.children
        ? this.configureActiveTab(
            { activeTabId, treeTab: tab.children },
            {
              isParentActive: isThisTabActive,
            },
          )
        : [];

      const hasActiveChild = children.some(child => child.isActive);
      const isActive = isThisTabActive || (hasActiveChild && !!activeTabId);

      return {
        ...tab,
        isActive,
        children,
      } satisfies TreeTabUI;
    });
    return result;
  }

  protected tabClick(tabId: string): void {
    this.activeItemId.set(tabId);
    this.tabChange.emit({ tabId });
    this.highlightSelectedItem();
    if (this.activeMenu) {
      this.activeMenu.set(false);
    }
  }

  private highlightSelectedItem(): void {
    timer(1).subscribe(() => {
      const allScrollTargetItems = document.querySelectorAll('.scroll-target-item');
      const currentChild = allScrollTargetItems[allScrollTargetItems.length - 1] as HTMLElement;
      if (this.scrollSectionElement && currentChild) {
        const scrollSection = this.scrollSectionElement.nativeElement;
        const offset = this.btnMenu?.nativeElement.offsetWidth ?? 0;
        scrollSection.scrollTo({
          left: this.displayBurgerNavigation()
            ? currentChild.offsetLeft - offset
            : currentChild.offsetLeft + (currentChild.offsetWidth - scrollSection.offsetWidth) / 2,
          behavior: 'smooth',
        });
      }
    });
  }

  toggleSearch(): void {
    this.searchEvent.emit();
  }

  toggleMenu(): void {
    this.activeMenu.set(!this.activeMenu());
    this.isBurgerNavigationOpen.emit(this.activeMenu());
  }

  public typedTplDataEntry = (data: TreeTabUI[], level: number) => ({
    data,
    level,
  });
  public typedTplData = (data: object) =>
    data as {
      data: TreeTabUI[];
      level: number;
    };

  protected getActiveTab = (tabs: TreeTabUI[]): TreeTabUI | undefined => {
    // Find active tab or return first tab as fallback
    return tabs.find(tab => tab.isActive) || tabs[0];
  };
}
